// World Map Component showing AI Startup coverage density by country
import { useState, useMemo } from "react";
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup
} from "react-simple-maps";
import { Globe, ZoomIn, ZoomOut, RotateCcw, Info } from "lucide-react";

// TopoJSON URL for world map
const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

// Country name mapping (some countries have different names in TopoJSON)
const COUNTRY_NAME_MAP = {
  "USA": "United States of America",
  "UK": "United Kingdom",
  "South Korea": "South Korea",
  "UAE": "United Arab Emirates",
  "DR Congo": "Dem. Rep. Congo",
  "Republic of the Congo": "Congo",
  "Ivory Coast": "Côte d'Ivoire",
  "Czech Republic": "Czechia",
  "North Macedonia": "Macedonia",
  "Eswatini": "eSwatini",
  "Timor-Leste": "East Timor",
  "Vatican City": "Vatican",
  "Bosnia and Herzegovina": "Bosnia and Herz.",
  "Central African Republic": "Central African Rep.",
  "South Sudan": "S. Sudan",
  "North Korea": "North Korea",
  "Trinidad and Tobago": "Trinidad and Tobago",
  "Saint Kitts and Nevis": "St. Kitts and Nevis",
  "Saint Lucia": "Saint Lucia",
  "Saint Vincent and the Grenadines": "St. Vin. and Gren.",
  "Antigua and Barbuda": "Antigua and Barb.",
  "Sao Tome and Principe": "São Tomé and Príncipe",
  "Equatorial Guinea": "Eq. Guinea",
  "Dominican Republic": "Dominican Rep.",
  "Solomon Islands": "Solomon Is.",
  "Marshall Islands": "Marshall Is.",
  "Papua New Guinea": "Papua New Guinea",
};

// Get color based on app count
const getCountryColor = (count) => {
  if (!count || count === 0) return "#1a1a2e"; // Dark - no data
  if (count === 1) return "#2d4a5e"; // Very low
  if (count <= 3) return "#3d6b80"; // Low
  if (count <= 10) return "#4d8fa8"; // Medium-low
  if (count <= 20) return "#5cb3c9"; // Medium
  if (count <= 50) return "#7dd3e8"; // Medium-high
  if (count <= 100) return "#a8e6f0"; // High
  return "#d4f5fa"; // Very high (USA, etc.)
};

export const WorldMapChart = ({ countryDistribution }) => {
  const [zoom, setZoom] = useState(1);
  const [center, setCenter] = useState([0, 20]);
  const [tooltipContent, setTooltipContent] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  // Create lookup map for quick access
  const countryLookup = useMemo(() => {
    const lookup = {};
    if (countryDistribution) {
      Object.entries(countryDistribution).forEach(([country, count]) => {
        // Add both original name and mapped name
        lookup[country] = count;
        if (COUNTRY_NAME_MAP[country]) {
          lookup[COUNTRY_NAME_MAP[country]] = count;
        }
      });
    }
    return lookup;
  }, [countryDistribution]);

  // Get stats
  const totalCountries = Object.keys(countryDistribution || {}).length;
  const totalApps = Object.values(countryDistribution || {}).reduce((a, b) => a + b, 0);
  const topCountries = Object.entries(countryDistribution || {})
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);

  const handleZoomIn = () => setZoom(Math.min(zoom * 1.5, 8));
  const handleZoomOut = () => setZoom(Math.max(zoom / 1.5, 1));
  const handleReset = () => { setZoom(1); setCenter([0, 20]); };

  const handleMouseEnter = (geo, event) => {
    const name = geo.properties.name;
    const count = countryLookup[name] || 0;
    
    // Also check original names
    let originalName = name;
    Object.entries(COUNTRY_NAME_MAP).forEach(([orig, mapped]) => {
      if (mapped === name) originalName = orig;
    });
    const actualCount = countryLookup[originalName] || countryLookup[name] || 0;
    
    setTooltipContent({
      name: originalName !== name ? originalName : name,
      count: actualCount
    });
    setTooltipPosition({ x: event.clientX, y: event.clientY });
  };

  const handleMouseLeave = () => {
    setTooltipContent(null);
  };

  return (
    <div className="bg-card border border-border rounded-lg p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Globe className="w-5 h-5 text-primary" />
          Global AI Startup Coverage
        </h3>
        <div className="flex items-center gap-2">
          <button
            onClick={handleZoomIn}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
            title="Zoom In"
            data-testid="map-zoom-in"
          >
            <ZoomIn className="w-4 h-4" />
          </button>
          <button
            onClick={handleZoomOut}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
            title="Zoom Out"
            data-testid="map-zoom-out"
          >
            <ZoomOut className="w-4 h-4" />
          </button>
          <button
            onClick={handleReset}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
            title="Reset View"
            data-testid="map-reset"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="bg-muted/30 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-violet-400">{totalCountries}</p>
          <p className="text-xs text-muted-foreground">Countries</p>
        </div>
        <div className="bg-muted/30 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-emerald-400">{totalApps}</p>
          <p className="text-xs text-muted-foreground">Total Apps</p>
        </div>
        <div className="bg-muted/30 rounded-lg p-3 text-center col-span-2">
          <p className="text-sm font-medium">Top Regions</p>
          <p className="text-xs text-muted-foreground">
            {topCountries.slice(0, 3).map(([c, n]) => `${c} (${n})`).join(", ")}
          </p>
        </div>
      </div>

      {/* Map Container */}
      <div className="relative bg-[#0a0a14] rounded-lg overflow-hidden" onMouseLeave={handleMouseLeave} style={{ height: "400px" }}>
        <ComposableMap
          projectionConfig={{
            rotate: [-10, 0, 0],
            scale: 147
          }}
          style={{ width: "100%", height: "100%" }}
        >
          <ZoomableGroup zoom={zoom} center={center} onMoveEnd={({ coordinates, zoom }) => { setCenter(coordinates); setZoom(zoom); }}>
            <Geographies geography={GEO_URL}>
              {({ geographies }) =>
                geographies.map((geo) => {
                  const name = geo.properties.name;
                  const count = countryLookup[name] || 0;
                  
                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      fill={getCountryColor(count)}
                      stroke="#2a2a4a"
                      strokeWidth={0.5}
                      onMouseEnter={(e) => handleMouseEnter(geo, e)}
                      onMouseLeave={handleMouseLeave}
                      style={{
                        default: { outline: "none" },
                        hover: { 
                          fill: count > 0 ? "#f59e0b" : "#3d3d5c",
                          outline: "none",
                          cursor: "pointer"
                        },
                        pressed: { outline: "none" }
                      }}
                    />
                  );
                })
              }
            </Geographies>
          </ZoomableGroup>
        </ComposableMap>

        {/* Tooltip */}
        {tooltipContent && (
          <div
            className="fixed z-50 px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl text-sm pointer-events-none"
            style={{
              left: tooltipPosition.x + 10,
              top: tooltipPosition.y - 40
            }}
          >
            <p className="font-semibold">{tooltipContent.name}</p>
            <p className="text-muted-foreground">
              {tooltipContent.count > 0 
                ? `${tooltipContent.count} AI app${tooltipContent.count > 1 ? 's' : ''}`
                : 'No data yet'}
            </p>
          </div>
        )}

        {/* Legend */}
        <div className="absolute bottom-3 left-3 bg-black/70 backdrop-blur-sm rounded-lg p-3 text-xs">
          <p className="font-medium mb-2 flex items-center gap-1">
            <Info className="w-3 h-3" /> App Density
          </p>
          <div className="flex items-center gap-1">
            <div className="w-4 h-3 rounded" style={{ backgroundColor: "#1a1a2e" }}></div>
            <span className="text-muted-foreground">0</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-3 rounded" style={{ backgroundColor: "#3d6b80" }}></div>
            <span className="text-muted-foreground">1-3</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-3 rounded" style={{ backgroundColor: "#5cb3c9" }}></div>
            <span className="text-muted-foreground">4-20</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-3 rounded" style={{ backgroundColor: "#a8e6f0" }}></div>
            <span className="text-muted-foreground">21-100</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-3 rounded" style={{ backgroundColor: "#d4f5fa" }}></div>
            <span className="text-muted-foreground">100+</span>
          </div>
        </div>
      </div>

      {/* Top Countries List */}
      <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-2">
        {topCountries.map(([country, count], idx) => (
          <div 
            key={country}
            className="bg-muted/20 rounded-lg p-2 text-center"
          >
            <span className="text-xs text-muted-foreground">#{idx + 1}</span>
            <p className="font-medium text-sm truncate">{country}</p>
            <p className="text-lg font-bold" style={{ color: getCountryColor(count) }}>{count}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default WorldMapChart;
